/**
 * NewTemplateDialog — admin uploads a new SOP/WI/BMR template (DRAFT).
 * Owner: Phase 7+ — V18 item A.
 */
import { useEffect, useState, type ChangeEvent } from 'react';
import { Button } from '@edms/design-system';
import { useCreateTemplate, useDepartments, type SopTemplate } from '../../api/orgHooks';
import styles from '../AuthorWorkbench/NewDocumentForm.module.css';

const DOC_TYPES = ['SOP', 'WI', 'POL', 'BMR', 'PRC', 'SPEC', 'FORM'];
const MAX_UPLOAD = 8 * 1024 * 1024;

function fileToB64(f: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => {
      const result = r.result as string;
      const idx = result.indexOf(',');
      resolve(idx >= 0 ? result.slice(idx + 1) : result);
    };
    r.onerror = () => reject(r.error);
    r.readAsDataURL(f);
  });
}

export function NewTemplateDialog({
  isOpen, onClose, onCreated,
}: {
  isOpen: boolean;
  onClose: () => void;
  onCreated: (t: SopTemplate) => void;
}) {
  const [code, setCode] = useState('');
  const [title, setTitle] = useState('');
  const [docType, setDocType] = useState('SOP');
  const [deptId, setDeptId] = useState('');
  const [desc, setDesc] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const m = useCreateTemplate();
  const depsQ = useDepartments();

  useEffect(() => {
    if (isOpen) {
      setCode(''); setTitle(''); setDocType('SOP'); setDeptId(''); setDesc(''); setFile(null); setErr(null);
    }
  }, [isOpen]);

  function onPick(e: ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0] ?? null;
    if (f && f.size > MAX_UPLOAD) { setErr('File exceeds 8 MB.'); return; }
    setErr(null); setFile(f);
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault(); setErr(null);
    if (!code.trim() || !title.trim()) { setErr('Code + title are required.'); return; }
    try {
      let artifactBase64: string | undefined;
      let artifactMime: string | undefined;
      let artifactFilename: string | undefined;
      if (file) {
        artifactBase64 = await fileToB64(file);
        artifactMime = file.type || 'application/octet-stream';
        artifactFilename = file.name;
      }
      const t = await m.mutateAsync({
        code: code.trim().toUpperCase(),
        title: title.trim(),
        docTypeCode: docType,
        owningDepartmentId: deptId || undefined,
        description: desc.trim() || undefined,
        artifactBase64, artifactMime, artifactFilename,
      });
      onCreated(t);
      onClose();
    } catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
  }

  if (!isOpen) return null;
  return (
    <div className={styles.backdrop} onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className={styles.dialog} role="dialog" aria-modal="true">
        <header className={styles.header}>
          <h2 className={styles.title}>New SOP template</h2>
          <p className={styles.desc}>Starts in DRAFT. Transition to APPROVED after QA review.</p>
        </header>
        <form className={styles.form} onSubmit={onSubmit}>
          {err && <div className={styles.errorBanner} role="alert">{err}</div>}
          <div className={styles.field}>
            <label className={styles.label}>Code<span className={styles.required}>*</span></label>
            <input className={styles.input} value={code} onChange={(e) => setCode(e.target.value)} placeholder="TPL-SOP-WHS-001" />
          </div>
          <div className={styles.field}>
            <label className={styles.label}>Title<span className={styles.required}>*</span></label>
            <input className={styles.input} value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Warehouse Receiving SOP" />
          </div>
          <div className={styles.field}>
            <label className={styles.label}>Doc type<span className={styles.required}>*</span></label>
            <select className={styles.select} value={docType} onChange={(e) => setDocType(e.target.value)}>
              {DOC_TYPES.map((d) => <option key={d} value={d}>{d}</option>)}
            </select>
          </div>
          <div className={styles.field}>
            <label className={styles.label}>Owning department</label>
            <select className={styles.select} value={deptId} onChange={(e) => setDeptId(e.target.value)}>
              <option value="">— Site-wide (no specific dept) —</option>
              {(depsQ.data?.items ?? []).filter((d) => d.isActive).map((d) => (
                <option key={d.departmentId} value={d.departmentId}>{d.name}</option>
              ))}
            </select>
          </div>
          <div className={styles.field}>
            <label className={styles.label}>Description</label>
            <textarea className={styles.input} rows={2} value={desc} onChange={(e) => setDesc(e.target.value)} placeholder="What is this template for?" />
          </div>
          <div className={styles.field}>
            <label className={styles.label}>Upload artefact (.docx / .pdf, ≤8 MB)</label>
            <input type="file" className={styles.input}
              accept=".doc,.docx,.pdf,.odt,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
              onChange={onPick} />
            {file && <div style={{ fontSize: 12, color: 'var(--color-text-tertiary, #6B7989)' }}><strong>{file.name}</strong> · {Math.round(file.size/1024)} KB</div>}
          </div>
          <footer className={styles.footer}>
            <Button type="button" variant="secondary" onClick={onClose}>Cancel</Button>
            <Button type="submit" variant="primary" isLoading={m.isPending}>Create draft</Button>
          </footer>
        </form>
      </div>
    </div>
  );
}
